/**
 * FlashBook — the stream the recorder is holding, borrowed for a moment.
 *
 * The recording screen's meter needs the creator's voice as a *number*, and on
 * the web the only place that number can come from honestly is the input the
 * recorder has open. `expo-audio` does not hand its stream out — the recorder is
 * a black box that answers with a URL once it has stopped — so the stream is
 * taken from the one moment it is visible: the call that opens it.
 *
 * So this borrows without opening anything. For the beat between arming and the
 * recorder asking for the microphone, `getUserMedia` is wrapped, the stream it
 * returns is kept, and the wrapper is removed again. **The stream is never
 * stopped here**: it belongs to the recorder, and a meter that closed the
 * microphone it was measuring would be trading a creator's recording for a
 * moving picture.
 *
 * That is why this exists at all rather than a second `getUserMedia`, which is
 * what the meter used to do. A second capture is a second device, and WebKit
 * gives an app one: there, opening the input twice can interrupt the track the
 * recorder is holding, so the meter was simply *switched off on Safari* — which
 * is the browser this app is most often read in on an iPhone. Borrowing the
 * recorder's own stream is safe everywhere, and it measures the thing being
 * recorded rather than a parallel view of it.
 */

/** The stream the wrapper caught, if it has caught one. */
let tapped: MediaStream | null = null

/** The real `getUserMedia`, while the wrapper is in place. */
let original: typeof navigator.mediaDevices.getUserMedia | null = null

/**
 * Prepare to borrow the next audio stream anyone opens.
 *
 * Called immediately before the recorder is prepared, and safe to call when
 * there is nothing to patch (a build without `mediaDevices`, a browser that has
 * never granted a microphone): the meter then has nothing to read and says so.
 */
export function armRecorderTap(): void {
  if (typeof navigator === 'undefined') return
  const devices = navigator.mediaDevices
  if (!devices || typeof devices.getUserMedia !== 'function') return
  if (original) return
  const real = devices.getUserMedia.bind(devices)
  original = real
  devices.getUserMedia = async (constraints?: MediaStreamConstraints) => {
    const stream = await real(constraints)
    /*
     * Only an audio capture is the recorder's. A page may open a camera for an
     * entirely different reason while this is armed, and borrowing that one would
     * give the meter a picture of a room instead of the voice being recorded.
     */
    if (constraints?.audio) {
      tapped = stream
      // One shot: the recorder does not open the input twice, and a wrapper left
      // in place is a wrapper that captures somebody else's microphone later.
      disarmRecorderTap()
    }
    return stream
  }
}

/** Put the platform's own `getUserMedia` back. */
export function disarmRecorderTap(): void {
  if (!original) return
  const real = original
  original = null
  try {
    navigator.mediaDevices.getUserMedia = real
  } catch {
    // A frozen `mediaDevices` cannot be patched back — and it cannot have been
    // patched at all, so there is nothing left wrapped.
  }
}

/**
 * The borrowed stream, without taking it.
 *
 * Two readers ask — the meter, for whether it can measure at all, and then its
 * sampling loop — and neither may consume it: the stream stays alive until the
 * recorder stops it.
 */
export function peekTappedStream(): MediaStream | null {
  return tapped
}

/** Forget the borrowed stream — the take is over, and the tracks are closed. */
export function forgetTappedStream(): void {
  tapped = null
}
